/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { type CSSProperties, forwardRef, type ReactNode, useContext } from 'react';

import { cssMap as unboundCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { AvatarContentContext } from './internal/content-context';
import { useEnsureIsInsideAvatar } from './internal/use-ensure-is-inside-avatar';

const boxShadowCssVar = '--avatar-box-shadow';
const bgColorCssVar = '--avatar-bg-color';

const styles = cssMap({
	root: {
		display: 'flex',
		position: 'static',
		alignItems: 'stretch',
		justifyContent: 'center',
		flexDirection: 'column',
		border: 'none',
		cursor: 'inherit',
		marginBlockEnd: token('space.025'),
		marginBlockStart: token('space.025'),
		marginInlineEnd: token('space.025'),
		marginInlineStart: token('space.025'),
		outline: 'none',
		overflow: 'hidden',
		paddingBlockEnd: token('space.0'),
		paddingBlockStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
		transform: 'translateZ(0)',
		transition: 'transform 200ms, opacity 200ms',
		'&::after': {
			width: '100%',
			position: 'absolute',
			inset: token('space.0'),
			backgroundColor: 'transparent',
			content: "' '",
			opacity: 0,
			pointerEvents: 'none',
			transition: 'opacity 200ms',
		},
		'&:focus-visible': {
			boxShadow: 'initial',
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
		'@media screen and (forced-colors: active), screen and (-ms-high-contrast: active)': {
			'&:focus-visible': {
				outlineWidth: token('border.width'),
			},
		},
	},
	circle: {
		borderRadius: token('radius.full', '50%'),
		'&::after': {
			borderRadius: token('radius.full', '50%'),
		},
	},
	square: {
		borderRadius: token('radius.tile'),
	},
	positionRelative: {
		position: 'relative',
	},
	disabled: {
		cursor: 'not-allowed',
		'&::after': {
			backgroundColor: token('elevation.surface'),
			opacity: token('opacity.disabled'),
		},
	},
});

const unboundStyles = unboundCssMap({
	root: {
		boxSizing: 'content-box',
		// NOTE: The `background` shorthand (rather than `background-color`) is intentional so that
		// `borderColor` can be any background value, including gradients, patterns, and images.
		background: `var(${bgColorCssVar})`,
		boxShadow: `var(${boxShadowCssVar})`,
	},
	hexagonFocusContainer: {
		backgroundColor: 'transparent',
		// NOTE: The left/right sides of this are intentionally inset by 10% on each side to be more visually appealing and work with circular images.
		clipPath: `polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)`,

		// NOTE: The `clip-path` changes padding in an unexpected way, so `1.25` is a magic number
		// to increase the padding on this outer layer, but keep `padding-inline` unchanged.
		// The goal here is emulating a 2px "outline"
		paddingBlock: `calc(${token('border.width.selected')} * 1.25)`,
		paddingInline: token('border.width.selected'),
		marginBlock: `calc(${token('border.width.selected')} * 1.25 * -1)`,
		marginInline: `calc(${token('border.width.selected')} * -1)`,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- We have to hack the focus together with this hexagon `clip-path`
		'&:has(:focus-visible)': {
			backgroundColor: token('color.border.focused'),
		},
	},
	updatedHexagonClipPath: {
		clipPath:
			'polygon(43.61555% 1.47441%, 46.08136% 0.49147%, 48.67910% 0.00000%, 51.32080% 0.00000%, 53.91847% 0.49147%, 56.38412% 1.47441%, 93.61555% 20.77327%, 95.79666% 22.19894%, 97.56964% 23.97299%, 98.89052% 26.02699%, 99.71530% 28.29251%, 100.00000% 30.70112%, 100.00000% 69.29883%, 99.71530% 71.70746%, 98.89052% 73.97300%, 97.56964% 76.02700%, 95.79666% 77.80100%, 93.61555% 79.22654%, 56.38412% 98.52540%, 53.91847% 99.50837%, 51.32080% 99.99991%, 48.67910% 100.00000%, 46.08136% 99.50860%, 43.61555% 98.52569%, 6.38428% 79.22654%, 4.20328% 77.80100%, 2.43035% 76.02700%, 1.10949% 73.97300%, 0.28471% 71.70746%, 0.00000% 69.29883%, 0.00000% 30.70112%, 0.28471% 28.29251%, 1.10949% 26.02699%, 2.43035% 23.97299%, 4.20328% 22.19894%, 6.38428% 20.77327%)',
	},
	hexagonBorderContainer: {
		// NOTE: The `background` shorthand (rather than `background-color`) is intentional so that
		// `borderColor` can be any background value, including gradients, patterns, and images.
		background: `var(${bgColorCssVar})`,
		clipPath: 'inherit',
		// NOTE: The `clip-path` and `background` overflows padding in an unexpected way, so
		// `0.5` and `0.4` are relatively magic numbers, but the ratio between block and inline is consistent.
		// The goal here is emulating a 2px "border"
		paddingBlock: `calc(${token('border.width.selected')} * 0.5)`,
		paddingInline: `calc(${token('border.width.selected')} * 0.4)`,
		marginBlock: `calc(${token('border.width.selected')} * 0.5 * -1)`,
		marginInline: `calc(${token('border.width.selected')} * 0.4 * -1)`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- We have to hack the focus together with this hexagon `clip-path`
		'&:has(:focus-visible)': {
			// NOTE: For `circle` and `square` this is different. This would be border:none` and
			// `outline:2px white` so `borer:none` or `box-shadow:none` would fall through to the background.
			// But we can't do that here and `background:transparent` would show the `hexagonFocusContainer` color instead
			// The goal here is emulating a `border:none` and showing `outline:2px white` with `outline-offset:2px`
			// as seen in `circle` and `square` appearances.
			backgroundColor: token('elevation.surface'),
		},
	},
	hexagon: {
		clipPath: 'inherit',
		// Drop some styles that don't work with `clip-path`…
		// Eg. we have to set outline and box shadows via wrappers due to `clip-path` complexities…
		boxShadow: 'unset',
		borderRadius: 0,
		'&::after': { borderRadius: 0 },
		'&:focus-visible': {
			boxShadow: 'unset',
			outlineWidth: 0,
		},
		'@media screen and (forced-colors: active), screen and (-ms-high-contrast: active)': {
			'&:focus-visible': {
				outlineWidth: 0,
			},
		},
	},
	interactive: {
		cursor: 'pointer',
		'&:hover::after': {
			backgroundColor: token('color.interaction.hovered'),
			opacity: '1',
		},
		'&:active::after': {
			backgroundColor: token('color.interaction.pressed'),
			opacity: '1',
		},
		'@media screen and (forced-colors: active)': {
			'&:focus-visible': {
				outlineWidth: 1,
			},
		},
	},
	// This can be combined with the interactive style when "platform-dst-motion-uplift" is cleaned up
	interactiveMotion: {
		transition: token('motion.avatar.hovered'),
		willChange: 'transform',
		'&:hover': {
			transform: 'scale(1.12)',
		},
	},
});

const hexagonBorderFixStyles = unboundCssMap({
	hexagonBorderContainer: {
		// The 'border' is actually stacked hexagons, with the inner hexagon inset.
		// A full 2px-looking clipped-hexagon ring needs `borderWidth * 1.12 = 2.24px` of
		// padding on each side (1.12 is calculated based on the hexagon geometry).
		// The existing avatar margin already contributes 2px of space.
		// See https://hello.atlassian.net/wiki/spaces/DST/pages/7432283012
		paddingBlockEnd: `calc(${token('border.width.selected')} * 0.12)`,
		paddingBlockStart: `calc(${token('border.width.selected')} * 0.12)`,
		paddingInlineEnd: `calc(${token('border.width.selected')} * 0.12)`,
		paddingInlineStart: `calc(${token('border.width.selected')} * 0.12)`,
		marginBlockEnd: `calc(${token('border.width.selected')} * -0.12)`,
		marginBlockStart: `calc(${token('border.width.selected')} * -0.12)`,
		marginInlineEnd: `calc(${token('border.width.selected')} * -0.12)`,
		marginInlineStart: `calc(${token('border.width.selected')} * -0.12)`,
	},
	hexagonFocusContainer: {
		// The focus ring is another stacked hexagon layer outside the border wrapper.
		// It does not get help from the inner avatar margin, so it needs the full
		// `borderWidth * 1.12` rectangular gap.
		// See https://hello.atlassian.net/wiki/spaces/DST/pages/7432283012
		paddingBlockEnd: `calc(${token('border.width.selected')} * 1.12)`,
		paddingBlockStart: `calc(${token('border.width.selected')} * 1.12)`,
		paddingInlineEnd: `calc(${token('border.width.selected')} * 1.12)`,
		paddingInlineStart: `calc(${token('border.width.selected')} * 1.12)`,
		marginBlockEnd: `calc(${token('border.width.selected')} * -1.12)`,
		marginBlockStart: `calc(${token('border.width.selected')} * -1.12)`,
		marginInlineEnd: `calc(${token('border.width.selected')} * -1.12)`,
		marginInlineStart: `calc(${token('border.width.selected')} * -1.12)`,
	},
});

// See https://hello.atlassian.net/wiki/spaces/DST/pages/7432283012
const updatedHexagonBorderFixStyles = unboundCssMap({
	hexagonBorderContainer: {
		// The existing avatar margin already contributes `borderWidth * 1` of the gap needed in
		// both directions, so no extra inline padding/margin is required, but the block direction
		// still needs the aspect-ratio-scaled remainder: `borderWidth * 1.125 - borderWidth * 1 = borderWidth * 0.125`.
		paddingBlockEnd: `calc(${token('border.width.selected')} * 0.125)`,
		paddingBlockStart: `calc(${token('border.width.selected')} * 0.125)`,
		paddingInlineEnd: 0,
		paddingInlineStart: 0,
		marginBlockEnd: `calc(${token('border.width.selected')} * -0.125)`,
		marginBlockStart: `calc(${token('border.width.selected')} * -0.125)`,
		marginInlineEnd: 0,
		marginInlineStart: 0,
	},
	hexagonFocusContainer: {
		// This layer gets no help from the inner avatar margin, so it needs the full gap in both
		// directions: `borderWidth * 1` inline, `borderWidth * 1.125` block.
		paddingBlockEnd: `calc(${token('border.width.selected')} * 1.125)`,
		paddingBlockStart: `calc(${token('border.width.selected')} * 1.125)`,
		paddingInlineEnd: token('border.width.selected'),
		paddingInlineStart: token('border.width.selected'),
		marginBlockEnd: `calc(${token('border.width.selected')} * -1.125)`,
		marginBlockStart: `calc(${token('border.width.selected')} * -1.125)`,
		marginInlineEnd: `calc(${token('border.width.selected')} * -1)`,
		marginInlineStart: `calc(${token('border.width.selected')} * -1)`,
	},
});

const widthHeightMap = cssMap({
	xxsmall: { width: '16px', height: '16px' },
	xsmall: { width: '16px', height: '16px' },
	UNSAFE_xsmall: { width: '20px', height: '20px' },
	small: { width: '24px', height: '24px' },
	medium: { width: '32px', height: '32px' },
	large: { width: '40px', height: '40px' },
	xlarge: { width: '96px', height: '96px' },
	xxlarge: { width: '128px', height: '128px' },
});

// The sole source of truth for the updated hexagon width/height geometry (per size).
const updatedHexagonDimensionMap = cssMap({
	xxsmall: { width: '15.4px', height: '17.17px' },
	small: { width: '23.11px', height: '25.76px' },
	medium: { width: '30.81px', height: '34.34px' },
	large: { width: '38.51px', height: '42.93px' },
	xlarge: { width: '92.44px', height: '103.04px' },
	xxlarge: { width: '123.25px', height: '137.39px' },
});

type AvatarContentProps = {
	children?: ReactNode;
};

/**
 * __Avatar content__
 *
 * Avatar content renders the avatar content. It can be composed with the Avatar component
 * to create a custom avatar.
 *
 * - [Examples](https://atlassian.design/components/avatar/examples)
 * - [Code](https://atlassian.design/components/avatar/code)
 * - [Usage](https://atlassian.design/components/avatar/usage)
 */
export const AvatarContent: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<AvatarContentProps> & React.RefAttributes<HTMLElement>
> = forwardRef<HTMLElement, AvatarContentProps>(({ children }, ref) => {
	useEnsureIsInsideAvatar();
	const {
		as: Container,
		appearance,
		UNSAFE_isUpdatedGeometry,
		avatarImage,
		borderColor = token('elevation.surface'),
		href,
		isDisabled,
		label,
		onClick,
		ref: contextRef,
		tabIndex,
		target,
		testId,
		size,
		stackIndex,
		'aria-controls': ariaControls,
		'aria-expanded': ariaExpanded,
		'aria-haspopup': ariaHasPopup,
	} = useContext(AvatarContentContext);

	const isInteractive = Boolean(onClick || href || isDisabled || ariaHasPopup);

	const renderedContent = (
		<Container
			css={[
				unboundStyles.root,
				styles.root,
				appearance === 'square' && styles.square,
				appearance === 'circle' && styles.circle,
				appearance === 'hexagon' && unboundStyles.hexagon,
				widthHeightMap[size],
				UNSAFE_isUpdatedGeometry &&
					updatedHexagonDimensionMap[
						size as 'xxsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'
					],
				stackIndex !== undefined && styles.positionRelative,
				isInteractive && !isDisabled && unboundStyles.interactive,
				isInteractive &&
					!isDisabled &&
					appearance !== 'hexagon' &&
					fg('platform-dst-motion-uplift') &&
					unboundStyles.interactiveMotion,
				isDisabled && styles.disabled,
			]}
			style={
				{
					[bgColorCssVar]: borderColor,
					[boxShadowCssVar]: `0 0 0 2px ${borderColor}`,
				} as CSSProperties
			}
			ref={
				(ref || contextRef) as React.Ref<HTMLAnchorElement & HTMLButtonElement & HTMLSpanElement>
			}
			aria-label={isInteractive ? label : undefined}
			aria-controls={ariaControls}
			aria-expanded={ariaExpanded}
			aria-haspopup={ariaHasPopup}
			onClick={onClick}
			tabIndex={tabIndex}
			data-testid={testId}
			disabled={isDisabled}
			type={Container === 'button' ? 'button' : undefined}
			{...(href && {
				href,
				target,
				rel: target === '_blank' ? 'noopener noreferrer' : undefined,
			})}
		>
			{children || avatarImage}
		</Container>
	);

	if (appearance !== 'hexagon') {
		return renderedContent;
	}

	// For a Hexagon Avatar in order to have hexagonal "border" and "outline", we have to
	// layer multiple elements and use their background colors to create the different layers.
	return (
		<div
			css={[
				unboundStyles.hexagonFocusContainer,
				UNSAFE_isUpdatedGeometry && unboundStyles.updatedHexagonClipPath,
				isInteractive &&
					!isDisabled &&
					fg('platform-dst-motion-uplift') &&
					unboundStyles.interactiveMotion,
				UNSAFE_isUpdatedGeometry && updatedHexagonBorderFixStyles.hexagonFocusContainer,
				fg('platform_editor_agent_mentions_drop_one_fixes') &&
					!UNSAFE_isUpdatedGeometry &&
					hexagonBorderFixStyles.hexagonFocusContainer,
			]}
			style={
				{
					[bgColorCssVar]: borderColor,
					[boxShadowCssVar]: `0 0 0 2px ${borderColor}`,
				} as CSSProperties
			}
			data-testid={testId ? `${testId}-hexagon-focus-container` : 'hexagon-focus-container'}
		>
			<div
				css={[
					unboundStyles.hexagonBorderContainer,
					UNSAFE_isUpdatedGeometry && updatedHexagonBorderFixStyles.hexagonBorderContainer,
					fg('platform_editor_agent_mentions_drop_one_fixes') &&
						!UNSAFE_isUpdatedGeometry &&
						hexagonBorderFixStyles.hexagonBorderContainer,
				]}
				data-testid={testId ? `${testId}-hexagon-border-container` : 'hexagon-border-container'}
			>
				{renderedContent}
			</div>
		</div>
	);
});
